// AccountMembersApi — the member READ (contract 22 §6.2 row 10), the shared seam behind the
// Permissions pane (ORP-18888), the Notifications recipient picker (ORP-18886) and the invite
// modal's duplicate detection (ORP-18889). v3 fetched this URL from two components and normalised
// it two different ways (`UsersPermissions.vue:509`, `NotificationUpdateModal.vue:247`); this class
// plus `service/memberList` is the reason v4 does not grow a third copy.
//
// Writes (invite / update / remove / revoke / transfer) are ORP-18888's — deliberately absent.
//
// `suppressGlobalErrorHandler`: a 403 here is a `forbidden` pane render, never a global toast (§6).
import type { AxiosInstance } from 'axios'

import type { AccountUsersDto } from '../models/member.types'

import { appHttp, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

export class AccountMembersApi {
  // Injectable for tests; defaults to the singleton instance.
  constructor(private readonly http: AxiosInstance = appHttp) {}

  private readonly cfg = { suppressGlobalErrorHandler: true } as const

  /**
   * `GET /accounts/{a}/users` — members + pending invites + per-workspace permissions in one
   * payload. **No trailing slash** (`accounts/urls.py:20` registers `path('users', …)`).
   *
   * `unwrapEnvelope`, not `unwrap`: the view returns `JsonResponse({'data': …})`
   * (`accounts/views.py:409-414`), so the containers are nested one level down. `unwrap` would
   * hand back `{data: {…}}` and every `.account_users` read would silently be `undefined` — the
   * exact failure §6.2's "envelope discipline" note warns about. Asserted in the spec.
   *
   * The body carries no `status` key, so `unwrapEnvelope`'s `status:'error'` throw never fires
   * here; a failure arrives as an HTTP status and therefore as an `ApiError` from the interceptor.
   *
   * ⚠ Unpaginated by design — every member, every invite, every workspace (BE-22-04).
   */
  async listMembers(accountId: number | string): Promise<AccountUsersDto> {
    const response = await this.http.get<ApiEnvelope<AccountUsersDto>>(
      `/accounts/${accountId}/users`,
      this.cfg,
    )
    return unwrapEnvelope(response) ?? {}
  }
}

export const accountMembersApi = new AccountMembersApi()
